/**
 * Omakase Vault SPA entry point. Tiny lit-html based shell that handles
 * routing, auth state, language toggle, and dispatches to page renderers.
 */

import { html, render, nothing, type TemplateResult } from "lit-html";

import { api, ApiError, onSessionExpired } from "./api.js";
import { clearUnlocked, restoreUnlocked, type UnlockedState } from "./client-crypto.js";
import { renderLogin } from "./pages/login.js";
import { renderGenesis } from "./pages/genesis.js";
import { renderVault } from "./pages/vault.js";
import { renderSettings } from "./pages/settings.js";
import { renderAdmin } from "./pages/admin.js";
import { renderApproveDevice } from "./pages/approve-device.js";
import { renderReset } from "./pages/reset.js";
import { renderInvite } from "./pages/invite.js";
import { renderFirstSetup } from "./pages/first-setup.js";

export type Route = "login" | "genesis" | "vault" | "settings" | "admin" | "approve-device" | "reset" | "invite" | "first-setup";

export interface AppState {
  route: Route;
  user: { id: string; email: string; name: string; role: string; must_change_pw?: boolean } | null;
  lang: "en" | "jp";
  mode: "view" | "print" | "admin";
  theme: "auto" | "light" | "dark";
  loading: boolean;
  error: string | null;
  unlockedAt: number | null;
  genesisCompleted: boolean;
}

const root = document.getElementById("app")!;

export const state: AppState = {
  route: "login",
  user: null,
  lang: (localStorage.getItem("omakase_accounts_lang") as "en" | "jp" | null) ?? "en",
  mode: "view",
  theme: (localStorage.getItem("omakase_accounts_theme") as "auto" | "light" | "dark" | null) ?? "auto",
  loading: true,
  error: null,
  unlockedAt: null,
  genesisCompleted: false,
};

export function setTheme(theme: AppState["theme"]): void {
  state.theme = theme;
  localStorage.setItem("omakase_accounts_theme", theme);
  if (theme === "auto") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", theme);
  }
  rerender();
}

export function setLang(lang: "en" | "jp"): void {
  state.lang = lang;
  localStorage.setItem("omakase_accounts_lang", lang);
  document.body.setAttribute("data-lang", lang);
  document.documentElement.setAttribute("lang", lang);
  rerender();
}

export function setMode(mode: AppState["mode"]): void {
  state.mode = mode;
  document.body.className = `mode-${mode}`;
  rerender();
}

export function setRoute(route: Route): void {
  state.route = route;
  rerender();
}

export function setUser(user: AppState["user"]): void {
  state.user = user;
  rerender();
}

export function setError(error: string | null): void {
  state.error = error;
  rerender();
}

export function setLoading(loading: boolean): void {
  state.loading = loading;
  rerender();
}

export function showToast(message: string, kind: "ok" | "error" = "ok"): void {
  const t = document.createElement("div");
  t.className = "toast";
  t.innerHTML = `<span class="status-dot is-${kind === "ok" ? "active" : "error"}"></span><span>${message}</span>`;
  document.body.appendChild(t);
  setTimeout(() => { t.style.opacity = "0"; t.style.transition = "opacity 200ms"; }, 1800);
  setTimeout(() => t.remove(), 2200);
}

export function copyToClipboard(text: string): void {
  void navigator.clipboard.writeText(text).then(
    () => showToast(state.lang === "en" ? "Copied" : "コピーしました", "ok"),
    () => showToast(state.lang === "en" ? "Copy failed" : "コピー失敗", "error"),
  );
}

function brandWordmark(): TemplateResult {
  return html`
    <span class="brand-wordmark" aria-label="Omakase Vault">
      <span class="word-omakase">O<span class="first-a">M</span>AKASE</span>
      <span class="word-vault">Vault</span>
    </span>
  `;
}

// SVG icons (Lucide-style monochrome stroke, currentColor) for topbar controls.
const SUN_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>`;
const MOON_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>`;
const AUTO_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M12 3a9 9 0 0 1 0 18"/></svg>`;
const LOGOUT_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>`;

function topbarTemplate(): TemplateResult {
  if (!state.user) return html``;
  const firstName = state.user.name?.split(" ")[0] ?? state.user.email;
  // Theme cycle: auto -> light -> dark -> auto. Icon reflects CURRENT theme.
  const themeIcon = state.theme === "auto" ? AUTO_ICON : state.theme === "light" ? SUN_ICON : MOON_ICON;
  const themeTitle = state.theme === "auto"
    ? (state.lang === "en" ? "Theme: Auto" : "テーマ: 自動")
    : state.theme === "light"
      ? (state.lang === "en" ? "Theme: Light" : "テーマ: ライト")
      : (state.lang === "en" ? "Theme: Dark" : "テーマ: ダーク");
  return html`
    <header class="topbar">
      <div class="brand">
        ${brandWordmark()}
      </div>
      <span class="greeting" title="${state.user.email}">
        <span data-lang="en">Welcome, ${firstName}</span><span data-lang="jp">${firstName}さん、ようこそ</span>
      </span>
      <div class="spacer"></div>

      <!-- Language pillbox: EN | 日本語 -->
      <div class="pillbox" role="tablist" aria-label="Language">
        <button class="pill ${state.lang === "en" ? "is-active" : ""}"
          @click=${() => state.lang !== "en" && setLang("en")}>EN</button>
        <button class="pill ${state.lang === "jp" ? "is-active" : ""}"
          @click=${() => state.lang !== "jp" && setLang("jp")}>日本語</button>
      </div>

      <!-- Navigation pillbox: First Setup | Vault | (Admin) | Settings -->
      <div class="pillbox" role="tablist" aria-label="Navigation">
        <button class="pill ${state.route === "first-setup" ? "is-active" : ""}"
          @click=${() => state.route !== "first-setup" && setRoute("first-setup")}>
          <span data-lang="en">First Setup</span><span data-lang="jp">初期設定</span>
        </button>
        <button class="pill ${state.route === "vault" ? "is-active" : ""}"
          @click=${() => state.route !== "vault" && setRoute("vault")}>
          <span data-lang="en">Vault</span><span data-lang="jp">ボルト</span>
        </button>
        ${state.user.role !== "EMPLOYEE"
          ? html`<button class="pill ${state.route === "admin" ? "is-active" : ""}"
              @click=${() => state.route !== "admin" && setRoute("admin")}>
              <span data-lang="en">Admin</span><span data-lang="jp">管理</span>
            </button>`
          : nothing}
        <button class="pill ${state.route === "settings" ? "is-active" : ""}"
          @click=${() => state.route !== "settings" && setRoute("settings")}>
          <span data-lang="en">Settings</span><span data-lang="jp">設定</span>
        </button>
      </div>

      <!-- Circular theme toggle (sun/moon/auto) -->
      <button class="icon-btn" title="${themeTitle}"
        @click=${() => {
          const next = state.theme === "auto" ? "light" : state.theme === "light" ? "dark" : "auto";
          setTheme(next);
        }}>${themeIcon}</button>

      <!-- Circular logout button -->
      <button class="icon-btn is-danger"
        title="${state.lang === "en" ? "Sign out" : "サインアウト"}"
        @click=${async () => {
          try { await api.logout(); } catch { /* ignore */ }
          clearUnlocked();
          setUser(null);
          setRoute("login");
        }}>${LOGOUT_ICON}</button>
    </header>
  `;
}

function shellTemplate(): TemplateResult {
  if (state.loading) {
    return html`<div class="login-shell"><div class="login-card"><p>
      <span data-lang="en">Loading...</span><span data-lang="jp">読み込み中...</span>
    </p></div></div>`;
  }
  // Force-gate: a user provisioned by the SU with a temporary master
  // password is must_change_pw=true. They can ONLY visit /settings until
  // they rotate the password. Any other route is rerouted to /settings.
  // This makes the must_change_pw flag actually load-bearing instead of
  // decorative (ARC iter-8 critical finding).
  if (state.user?.must_change_pw === true
      && state.route !== "settings"
      && state.route !== "login") {
    state.route = "settings";
  }
  const page = (() => {
    switch (state.route) {
      case "login": return renderLogin();
      case "genesis": return renderGenesis();
      case "vault": return renderVault();
      case "settings": return renderSettings();
      case "admin": return renderAdmin();
      case "approve-device": return renderApproveDevice();
      case "reset": return renderReset();
      case "invite": return renderInvite();
      case "first-setup": return renderFirstSetup();
      default: return html`<p>
        <span data-lang="en">Unknown route</span><span data-lang="jp">不明なルート</span>
      </p>`;
    }
  })();
  return html`
    ${topbarTemplate()}
    ${page}
  `;
}

export function rerender(): void {
  // Expose state to lazy template inspection (login.ts hides First-Setup
  // link based on genesisCompleted).
  (window as unknown as { __app_state?: AppState }).__app_state = state;
  render(shellTemplate(), root);
}

// =============================================================================
// Bootstrap
// =============================================================================

async function injectSpriteOnce(): Promise<void> {
  // Pull the SVG sprite into the DOM so vault cards can reference symbols
  // via <svg><use href="#ico-..."/></svg>. We fetch + inline rather than
  // <img src="/sprite.svg"> because external sprite refs are blocked by
  // some browsers' SVG sandboxing.
  if (document.getElementById("ovault-sprite-root")) return;
  try {
    const res = await fetch("/sprite.svg");
    if (!res.ok) return;
    const text = await res.text();
    const div = document.createElement("div");
    div.id = "ovault-sprite-root";
    div.style.cssText = "position:absolute;width:0;height:0;overflow:hidden";
    div.setAttribute("aria-hidden", "true");
    div.innerHTML = text;
    document.body.insertBefore(div, document.body.firstChild);
  } catch {
    // best-effort: cards fall back to initials when sprite missing
  }
}

// Wire the global "your session was revoked" hook: wipe the unlocked
// material from memory + localStorage and bounce to /login. Triggered
// by any API call returning 401 with session_missing/expired/revoked.
onSessionExpired(() => {
  clearUnlocked();
  if (state.user) {
    state.user = null;
    state.route = "login";
    rerender();
    showToast("Your session was revoked. Please sign in again.", "error");
  }
});

async function bootstrap(): Promise<void> {
  document.body.setAttribute("data-lang", state.lang);
  document.documentElement.setAttribute("lang", state.lang);
  if (state.theme === "light" || state.theme === "dark") {
    document.documentElement.setAttribute("data-theme", state.theme);
  }
  void injectSpriteOnce();
  // Restore unlocked key material from sessionStorage so a page reload
  // does not force a master-password re-prompt.
  const restored = restoreUnlocked();
  if (restored) {
    (window as unknown as { __unlocked?: UnlockedState }).__unlocked = restored;
  }

  // Path-based pre-routing for magic-link landings. These short-circuit
  // the /me probe so an unauthenticated user landing on /invite/<id>
  // sees the invite acceptance flow rather than the login screen.
  const path = window.location.pathname;
  if (path.startsWith("/invite/")) {
    state.route = "invite";
    state.loading = false;
    rerender();
    return;
  }
  if (path.startsWith("/approve-device")) {
    state.route = "approve-device";
    state.loading = false;
    rerender();
    return;
  }
  if (path.startsWith("/reset")) {
    state.route = "reset";
    state.loading = false;
    rerender();
    return;
  }

  // Genesis probe in parallel with /me so we have the right login UX
  // (hide "First setup" once SU exists).
  const [meResult, genResult] = await Promise.allSettled([api.me(), api.genesisStatus()]);

  if (genResult.status === "fulfilled") {
    state.genesisCompleted = genResult.value.genesis_completed === true;
  }

  if (meResult.status === "fulfilled") {
    const u = meResult.value.user as { id: string; email: string; name: string; role: string; must_change_pw?: boolean };
    state.user = {
      id: u.id, email: u.email, name: u.name, role: u.role,
      must_change_pw: Boolean(u.must_change_pw),
    };
    state.route = u.must_change_pw ? "settings" : "vault";
  } else {
    const err = meResult.reason;
    if (err instanceof ApiError && err.code === "session_missing") {
      state.route = state.genesisCompleted ? "login" : "genesis";
    } else {
      state.error = (err as Error)?.message ?? "Bootstrap failure";
      state.route = "login";
    }
  }
  state.loading = false;
  rerender();
  // Heartbeat: silently re-check session every 60s. If the server has
  // revoked it (admin revoked the device, manual session revoke, ttl
  // expiry), api.me() throws 401 -> the global onSessionExpired hook
  // wipes localStorage + bounces to /login. Without this the user can
  // keep reading decrypted vault items forever from the cached unlock.
  if (state.user) {
    setInterval(() => {
      if (state.user) void api.me().catch(() => { /* hook handled it */ });
    }, 60_000);
  }
}

void bootstrap();
